import React from 'react';
import Description from '@material-ui/icons/Description';
import HowToReg from '@material-ui/icons/HowToReg';
import AttachMoney from '@material-ui/icons/AttachMoney';
import { fmtDateBr, startOfToday } from '../../../utils/monthRef';
import {
  CARD_DEADLINE_ROWS,
  daysUntilDue,
  getStageUiColors,
  prodStatusLabel,
  productDisplayName,
  statusId,
} from './dashboardEntregasHelpers';

const ICON_BY_KEY = {
  entrega: Description,
  aprovacao: HowToReg,
  faturar: AttachMoney,
};

const StatusPill = ({ value }) => {
  const id = Number(value) || 0;
  const bg = id === 8
    ? '#5664d2'
    : id === 7
      ? '#1cbb8c'
      : id === 6
        ? '#f1b44c'
        : id === 9
          ? '#ff5757'
          : '#4e515e';
  return (
    <span
      style={{
        fontSize: 11,
        padding: '2px 8px',
        borderRadius: 999,
        color: '#fff',
        backgroundColor: bg,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      {prodStatusLabel(id) || '—'}
    </span>
  );
};

const DeadlineStack = ({ product, today }) => (
  <div className="d-flex flex-column" style={{ gap: 3, flexShrink: 0, minWidth: 108 }}>
    {CARD_DEADLINE_ROWS.map(({ key, field, label }) => {
      const Icon = ICON_BY_KEY[key];
      const raw = product?.[field];
      const { dateColor, iconColor, late, overduePending, fulfilled } = getStageUiColors(
        product,
        key,
        today,
      );
      const emphasize = late || overduePending || fulfilled;
      const titleBits = [label];
      if (fulfilled && late) titleBits.push('cumprida com atraso');
      else if (fulfilled) titleBits.push('cumprida no prazo');
      else if (overduePending) titleBits.push('em atraso');
      else titleBits.push('em dia');

      return (
        <div
          key={key}
          title={titleBits.join(' — ')}
          className="d-flex align-items-center"
          style={{ gap: 4, justifyContent: 'flex-end' }}
        >
          {Icon ? (
            <Icon
              style={{
                fontSize: 14,
                color: iconColor,
              }}
            />
          ) : null}
          <span
            style={{
              fontSize: 11,
              fontWeight: emphasize ? 700 : 600,
              color: dateColor,
              whiteSpace: 'nowrap',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {fmtDateBr(raw) || '—'}
          </span>
        </div>
      );
    })}
  </div>
);

/**
 * Card somente leitura do Kanban (sem drag-and-drop).
 */
export default function KanbanEntregaCard({
  product,
  viewKey,
  today,
  selected,
  onClick,
}) {
  const dayRef = today || startOfToday();
  const dias = daysUntilDue(product, viewKey, dayRef);
  const overdue = dias != null && dias < 0;
  const [hovered, setHovered] = React.useState(false);

  const border = selected
    ? '2px solid #4e515e'
    : hovered
      ? '2px solid #505A62'
      : overdue
        ? '2px solid rgba(255,87,87,0.55)'
        : '1px solid #e9ecef';

  return (
    <div
      role="button"
      tabIndex={0}
      draggable={false}
      aria-pressed={!!selected}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (typeof onClick === 'function') onClick();
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        border,
        borderRadius: 8,
        padding: '10px 12px',
        background: overdue ? 'rgba(255,87,87,0.06)' : '#fff',
        boxShadow: selected ? '0 4px 14px rgba(0,0,0,0.08)' : undefined,
        cursor: 'pointer',
        transition: 'border 0.18s ease, box-shadow 0.18s ease',
        userSelect: 'none',
      }}
    >
      <div
        className="d-flex justify-content-between align-items-start"
        style={{ gap: 8, marginBottom: 6 }}
      >
        <div
          style={{
            fontWeight: 700,
            fontSize: 13,
            color: '#4e515e',
            lineHeight: 1.3,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={productDisplayName(product)}
        >
          {productDisplayName(product)}
        </div>
        <DeadlineStack product={product} today={dayRef} />
      </div>
      <div className="d-flex justify-content-between align-items-center" style={{ gap: 8 }}>
        <div
          style={{
            fontSize: 12,
            color: '#6c757d',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={product?.proj_nome || ''}
        >
          {product?.proj_nome || '—'}
        </div>
        <StatusPill value={statusId(product)} />
      </div>
    </div>
  );
}
